import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Search, Repeat, ShieldCheck, Ship, Check } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Seo } from "@/components/common/Seo";
import { InsightsTeaser } from "@/components/common/InsightsTeaser";
import { HOME, PRODUCT_AREAS, CAPABILITIES, IMAGES, SEO } from "@/content/site";

const ICONS = { Search, Repeat, ShieldCheck, Ship };

const Hero = () => {
  const reduce = useReducedMotion();
  const h = HOME.hero;
  const fade = (delay) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay },
  });
  return (
    <section className="relative min-h-[82vh] overflow-hidden bg-azeka-navy flex items-center">
      <img src={IMAGES.hero} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-azeka-navy via-azeka-navy/92 to-azeka-navy/55" />
      <div className="grain absolute inset-0 opacity-40" />
      <div className="container-x relative pt-32 pb-20">
        <div className="max-w-4xl">
          <motion.p className="eyebrow mb-5 text-azeka-teal2" {...fade(0)}>
            {h.eyebrow}
          </motion.p>
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.08]"
            {...fade(0.08)}
          >
            {h.title}
          </motion.h1>
          <motion.p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300" {...fade(0.16)}>
            {h.subtitle}
          </motion.p>
          <motion.div className="mt-9 flex flex-wrap items-center gap-4" {...fade(0.24)}>
            <Link
              to={h.primary.to}
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-azeka-teal px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-azeka-teal2 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-azeka-teal2 focus:ring-offset-2 focus:ring-offset-azeka-navy active:scale-95"
            >
              {h.primary.label} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={h.secondary.to}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-0.5"
            >
              {h.secondary.label}
            </Link>
          </motion.div>
        </div>

        <motion.div className="mt-12 border-t border-white/15 pt-6" {...fade(0.32)}>
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-slate-200" data-testid="hero-credibility">
            {HOME.credibility.map((item, i) => (
              <li key={item} className="flex items-center gap-4">
                {i > 0 && <span className="text-azeka-teal2/70" aria-hidden="true">·</span>}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => {
  const i = HOME.intro;
  return (
    <section className="container-x py-16 lg:py-24">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <SectionHeading eyebrow={i.eyebrow} title={i.title} body={i.body} />
        </div>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="relative overflow-hidden rounded-2xl">
            <img src={IMAGES.docsReview} loading="lazy" decoding="async" alt="Trade partners reviewing commercial documentation" className="h-[360px] w-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-2xl" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Capabilities = () => {
  const c = HOME.capabilities;
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="container-x">
        <SectionHeading eyebrow={c.eyebrow} title={c.title} body={c.body} />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CAPABILITIES.map((cap, idx) => {
            const Icon = ICONS[cap.icon];
            return (
              <Reveal key={cap.title} delay={idx * 0.08}>
                <div className="group h-full rounded-2xl border border-slate-200/80 bg-surface-base p-7 transition-all duration-300 hover:-translate-y-1 hover:border-azeka-teal/40 hover:shadow-xl hover:shadow-azeka-navy/5">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-azeka-navy text-azeka-teal2 transition-colors group-hover:bg-azeka-teal group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-ink-heading">{cap.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-body">{cap.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProductAreas = () => {
  const p = HOME.productAreas;
  return (
    <section className="py-16 lg:py-24">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow={p.eyebrow} title={p.title} body={p.body} />
          <Reveal>
            <Link
              to="/product-areas"
              data-testid="home-product-areas-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-azeka-navy hover:text-azeka-teal transition-colors"
            >
              View all product areas <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCT_AREAS.map((area, idx) => (
            <Reveal key={area.id} delay={idx * 0.08}>
              <Link to="/product-areas" className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-azeka-navy/10">
                <div className="relative h-48 overflow-hidden">
                  <img src={area.image} loading="lazy" decoding="async" alt={area.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-azeka-navy/40 to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-base font-semibold text-ink-heading">{area.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-muted">{area.summary}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Brands = () => {
  const b = HOME.brands;
  return (
    <section className="bg-azeka-navy py-16 lg:py-24 relative overflow-hidden">
      <div className="grain absolute inset-0 opacity-30" />
      <div className="container-x relative grid gap-12 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <SectionHeading eyebrow={b.eyebrow} title={b.title} body={b.body} dark />
          <Reveal delay={0.1}>
            <a
              href={b.cta.to}
              data-testid="home-brands-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-azeka-teal px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-azeka-teal2 hover:-translate-y-0.5 active:scale-95"
            >
              {b.cta.label} <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            <img src={IMAGES.spaLeaf} loading="lazy" decoding="async" alt="Azeka Health — Pure Wellbeing" className="h-[320px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-azeka-navy/70 via-transparent to-transparent" />
            <span className="absolute left-5 top-5 inline-flex items-center rounded-full bg-azeka-amber/95 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-azeka-navy">
              {b.status}
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Audience = () => {
  const a = HOME.audience;
  return (
    <section className="container-x py-16 lg:py-24">
      <div className="grid gap-6 md:grid-cols-2">
        {[
          { d: a.suppliers, img: IMAGES.freight, testid: "home-suppliers" },
          { d: a.buyers, img: IMAGES.warehouse, testid: "home-buyers" },
        ].map((item, idx) => (
          <Reveal key={item.testid} delay={idx * 0.1}>
            <div className="group relative h-full overflow-hidden rounded-2xl bg-azeka-navy2 p-8 lg:p-10">
              <img src={item.img} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-azeka-navy via-azeka-navy/85 to-azeka-navy/60" />
              <div className="relative">
                <p className="eyebrow text-azeka-teal2">{a.eyebrow}</p>
                <h3 className="mt-3 text-2xl font-semibold text-white">{item.d.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300">{item.d.body}</p>
                <Link
                  to={item.d.cta.to}
                  data-testid={`${item.testid}-cta`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-azeka-teal2 hover:text-white transition-colors"
                >
                  {item.d.cta.label} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Quality = () => {
  const q = HOME.quality;
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="container-x grid gap-12 lg:grid-cols-12 lg:items-center">
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <div className="relative overflow-hidden rounded-2xl">
            <img src={IMAGES.pharmaLab} loading="lazy" decoding="async" alt="Quality systems and laboratory standards" className="h-[420px] w-full object-cover" />
          </div>
        </Reveal>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <SectionHeading eyebrow={q.eyebrow} title={q.title} body={q.body} />
          <ul className="mt-8 space-y-3">
            {q.points.map((pt, idx) => (
              <Reveal key={pt} delay={idx * 0.06}>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-azeka-sage text-azeka-teal">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-sm text-ink-body">{pt}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.2}>
            <Link
              to={q.cta.to}
              data-testid="home-quality-cta"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-azeka-navy hover:text-azeka-teal transition-colors"
            >
              {q.cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => {
  const c = HOME.cta;
  return (
    <section className="relative overflow-hidden bg-azeka-navy">
      <img src={IMAGES.portDusk} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-azeka-navy via-azeka-navy/90 to-azeka-teal/20" />
      <div className="container-x relative py-16 lg:py-20 text-center">
        <Reveal className="mx-auto max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-semibold text-white">{c.title}</h2>
          <p className="mt-4 text-base text-slate-300">{c.body}</p>
          <Link
            to={c.primary.to}
            data-testid="home-cta-band"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-azeka-teal px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-azeka-teal2 hover:-translate-y-0.5 active:scale-95"
          >
            {c.primary.label} <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo title={SEO.home.t} description={SEO.home.d} path="/" />
      <Hero />
      <Intro />
      <Capabilities />
      <ProductAreas />
      <Brands />
      <Audience />
      <Quality />
      <InsightsTeaser />
      <CtaBand />
    </div>
  );
}
